import { useState } from "react";
import { Navigate, Outlet, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useStaffRoles, type StaffRole } from "@/hooks/useStaffRoles";
import { Loader2, LayoutDashboard, CreditCard, Building2, Users, Package, FileText, ShieldCheck, Globe, HelpCircle, LogOut, ArrowLeft, Menu, Database, Stethoscope, Bell, LifeBuoy, Flag, Receipt, Settings2, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useTranslation } from "react-i18next";
import { useForceLightTheme } from "@/hooks/useForceLightTheme";
import { BackButton } from "./BackButton";
import NotFound from "@/pages/NotFound";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";

import { tr } from "@/lib/tr";
interface NavItem {
  to: string;
  label: () => string;
  icon: any;
  roles: StaffRole[];
}

const NAV: NavItem[] = [
  { to: "/admin", label: () => tr("الرئيسية", "Dashboard", "Tableau de bord"), icon: LayoutDashboard, roles: ["super_admin", "support"] },
  { to: "/admin/subscriptions", label: () => tr("الاشتراكات", "Subscriptions", "Abonnements"), icon: CreditCard, roles: ["super_admin", "support"] },
  { to: "/admin/companies", label: () => tr("الشركات", "Companies", "Sociétés"), icon: Building2, roles: ["super_admin", "support"] },
  { to: "/admin/users", label: () => tr("المستخدمون", "Users", "Utilisateurs"), icon: Users, roles: ["super_admin", "support"] },
  { to: "/admin/plans", label: () => tr("الباقات", "Plans", "Forfaits"), icon: Package, roles: ["super_admin"] },
  { to: "/admin/content", label: () => tr("محتوى الموقع", "Site Content", "Contenu du site"), icon: Globe, roles: ["super_admin", "content_editor"] },
  { to: "/admin/help", label: () => tr("مركز المساعدة", "Help Center", "Centre d'aide"), icon: HelpCircle, roles: ["super_admin", "content_editor", "support"] },
  { to: "/admin/help-drafts", label: () => tr("مسودات المساعدة", "Help Drafts", "Brouillons d'aide"), icon: FileText, roles: ["super_admin", "content_editor", "support"] },

  { to: "/admin/support", label: () => tr("الدعم الفني", "Support Tickets", "Support"), icon: LifeBuoy, roles: ["super_admin", "support"] },
  { to: "/admin/audit", label: () => tr("سجل التدقيق", "Audit Log", "Journal d'audit"), icon: FileText, roles: ["super_admin"] },
  { to: "/admin/data-integrity", label: () => tr("سلامة البيانات", "Data Integrity", "Intégrité des données"), icon: Stethoscope, roles: ["super_admin"] },
  { to: "/admin/staff", label: () => tr("الموظفون", "Staff", "Personnel"), icon: ShieldCheck, roles: ["super_admin"] },
  { to: "/admin/backup", label: () => tr("النسخ الاحتياطية", "Backups", "Sauvegardes"), icon: Database, roles: ["super_admin"] },
  { to: "/admin/notifications", label: () => tr("الإشعارات", "Notifications", "Notifications"), icon: Bell, roles: ["super_admin"] },
  { to: "/admin/payments", label: () => tr("مدفوعات الاشتراكات", "Subscription payments", "Paiements d'abonnement"), icon: Receipt, roles: ["super_admin"] },
  { to: "/admin/billing-settings", label: () => tr("إعدادات الدفع", "Payment settings", "Paramètres de paiement"), icon: Settings2, roles: ["super_admin"] },
  { to: "/admin/support-guides", label: () => tr("أدلة مساعد الدعم", "Assistant guides", "Guides assistant"), icon: BookOpen, roles: ["super_admin", "support"] },
  { to: "/admin/features", label: () => tr("Feature Flags", "Feature Flags", "Feature Flags"), icon: Flag, roles: ["super_admin"] },
];

const AdminSidebarBody = ({
  user, roles, visibleNav, signOut, onNavigate,
}: {
  user: any; roles: StaffRole[]; visibleNav: NavItem[];
  signOut: () => void; onNavigate?: () => void;
}) => (
  <div className="flex flex-col h-full bg-card">
    <div className="p-5 border-b border-border">
      <div className="font-display text-xl text-primary">SIDA Admin</div>
      <div className="text-xs text-muted-foreground mt-1">{tr("بوابة الإدارة الداخلية", "Internal admin portal", "Portail d'administration interne")}</div>
    </div>
    <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
      {visibleNav.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === "/admin"}
          onClick={() => onNavigate?.()}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
              isActive ? "bg-primary/10 text-primary border border-primary/20" : "text-foreground hover:bg-muted/60"
            }`
          }
        >
          <item.icon className="h-4 w-4" />
          {item.label()}
        </NavLink>
      ))}
    </nav>
    <div className="p-3 border-t border-border space-y-2">
      <div className="px-3 py-2 text-xs text-muted-foreground">
        <div className="truncate">{user.email}</div>
        <div className="mt-1 flex flex-wrap gap-1">
          {roles.map((r) => (
            <span key={r} className="px-1.5 py-0.5 bg-muted rounded text-primary text-[10px]">{r}</span>
          ))}
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="w-full justify-start text-foreground hover:text-primary hover:bg-muted"
        onClick={() => (window.location.href = "/dashboard")}
      >
        <ArrowLeft className="h-4 w-4 me-2" />
        {tr("العودة إلى التطبيق", "Back to App", "Retour à l'application")}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="w-full justify-start text-muted-foreground hover:text-foreground hover:bg-muted"
        onClick={() => signOut()}
      >
        <LogOut className="h-4 w-4 me-2" />
        {tr("تسجيل الخروج", "Sign out", "Se déconnecter")}
      </Button>
    </div>
  </div>
);

export const AdminLayout = () => {
  const { user, loading: authLoading, signOut } = useAuth();
  const { data: roles, isLoading } = useStaffRoles();
  const { i18n } = useTranslation();

  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const side = i18n.dir() === "rtl" ? "right" : "left";

  // Force light theme inside admin portal (clean admin identity)
  useForceLightTheme();

  // Wait for the session to hydrate before deciding — otherwise a direct visit
  // to /admin bounces to /auth and then straight to /dashboard.
  if (authLoading) {
    return <div className="flex h-screen items-center justify-center bg-muted/40"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }
  if (!user) return <Navigate to="/auth" state={{ from: location }} replace />;
  if (isLoading) {
    return <div className="flex h-screen items-center justify-center bg-muted/40"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }


  // Not a staff member → 404 (don't reveal portal exists)
  if (!roles || roles.length === 0) return <NotFound />;

  const visibleNav = NAV.filter((item) => item.roles.some((r) => roles.includes(r)));

  return (
    <div className="min-h-screen flex bg-muted/40 text-foreground">
      <aside className="hidden md:flex w-64 shrink-0 bg-card border-e border-border flex-col">
        <AdminSidebarBody user={user} roles={roles} visibleNav={visibleNav} signOut={signOut} />
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <header className="md:hidden sticky top-0 z-30 h-14 bg-card border-b border-border flex items-center px-3 gap-2 shadow-sm">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side={side} className="p-0 w-72">
              <AdminSidebarBody user={user} roles={roles} visibleNav={visibleNav} signOut={signOut} onNavigate={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>
          <BackButton fallback="/admin" iconOnly />
          <div className="font-display text-base text-primary">SIDA Admin</div>
        </header>

        {/* Desktop sub-header with global Back action (hidden on admin root) */}
        {location.pathname !== "/admin" && (
          <header className="hidden md:flex sticky top-0 z-30 h-12 bg-card/80 backdrop-blur border-b border-border items-center px-4 gap-2">
            <BackButton fallback="/admin" />
          </header>
        )}

        <main className="flex-1 overflow-x-auto p-3 sm:p-4 md:p-6">
          <RouteErrorBoundary><Outlet /></RouteErrorBoundary>
        </main>
      </div>
    </div>
  );
};
